import { useCallback } from "react";
import { useNavigate } from "react-router";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  APPOINTMENT_STATUS_LABELS,
  type AgendaSettings,
  type AppointmentStatus,
} from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";

type AppointmentLike = {
  id: string;
  patientName: string;
  date?: Date | string;
  startTime?: string;
  endTime?: string;
  salaId?: string | null;
};

/**
 * Hook com mutations e handlers de agendamento (move/status/delete).
 * Centraliza confirmações (com respeito a settings.confirmOnDrag) e toasts.
 */
export function useAgendaMutations({
  settings,
  findAppointment,
}: {
  settings: AgendaSettings | undefined;
  findAppointment: (id: string) => AppointmentLike | undefined;
}) {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const moveMutation = trpc.appointment.move.useMutation({
    // Optimistic: atualiza TODAS as queries listByRange em cache imediatamente
    // usando o queryClient (tRPC utils nao tem API pra tudo-de-uma-vez).
    onMutate: async (vars) => {
      await utils.appointment.listByRange.cancel();
      // Trocou de sala arrastando (agenda por sala): o card já aparece na coluna nova.
      const sala =
        vars.salaId === undefined
          ? undefined
          : { salaId: vars.salaId, sala: ((utils.salas.listar.getData() ?? []) as any[]).find((s) => s.id === vars.salaId) ?? null };
      queryClient.setQueriesData(
        { queryKey: [["appointment", "listByRange"]] },
        (old: unknown) => {
          if (!old || !Array.isArray(old)) return old;
          return old.map((a: any) =>
            a.id === vars.id
              ? {
                  ...a,
                  date: vars.date,
                  startTime: vars.startTime,
                  endTime: vars.endTime,
                  ...sala,
                }
              : a,
          );
        },
      );
    },
    onError: (e) => {
      toast.error(e.message || "Erro ao remarcar");
      utils.appointment.invalidate();
    },
    onSettled: () => utils.appointment.invalidate(),
  });

  const updateMutation = trpc.appointment.update.useMutation({
    onSuccess: () => utils.appointment.invalidate(),
    onError: (e) => toast.error(e.message || "Erro ao atualizar"),
  });

  const removeMutation = trpc.appointment.remove.useMutation({
    onSuccess: () => {
      toast.success("Consulta excluída");
      utils.appointment.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir"),
  });

  // Atendimento mexe no status da consulta E em "quem está ocupado agora" — daí invalidar a
  // agenda inteira (que inclui `emAtendimento`) mais a Equipe, que mostra o mesmo selo.
  const invalidarAtendimento = () => {
    utils.appointment.invalidate();
    utils.team.list.invalidate();
  };
  const iniciarMutation = trpc.appointment.iniciarAtendimento.useMutation({
    onSuccess: (a: any) => {
      toast.success(`Atendimento de ${a?.patientName ?? "paciente"} iniciado.`);
      invalidarAtendimento();
      // Iniciar leva pra ficha em modo atendimento — a tela de trabalho do dentista. Consulta de
      // paciente avulso (sem cadastro) não tem ficha pra abrir: fica só o status na agenda.
      if (a?.patientId) navigate(`/pacientes/${a.patientId}?atendimento=${a.id}`);
    },
    onError: (e) => toast.error(e.message || "Erro ao iniciar o atendimento"),
  });
  const encerrarMutation = trpc.appointment.encerrarAtendimento.useMutation({
    onSuccess: (a: any) => {
      toast.success(`Atendimento de ${a?.patientName ?? "paciente"} encerrado.`);
      invalidarAtendimento();
    },
    onError: (e) => toast.error(e.message || "Erro ao encerrar o atendimento"),
  });

  const handleMove = useCallback(
    async (id: string, newDate: Date, newStart: string, newEnd: string, salaId?: string | null) => {
      const appt = findAppointment(id);
      const prevDate = appt?.date ? new Date(appt.date) : null;
      const prevStart = appt?.startTime;
      const prevEnd = appt?.endTime;
      // Só manda a sala quando ela muda; o "Desfazer" devolve a anterior.
      const trocouDeSala = salaId !== undefined && salaId !== (appt?.salaId ?? null);
      const prevSala = trocouDeSala ? (appt?.salaId ?? null) : undefined;
      const nomeDaSala = trocouDeSala ? (((utils.salas.listar.getData() ?? []) as any[]).find((s) => s.id === salaId)?.nome ?? "Sem sala") : null;

      if (settings?.confirmOnDrag) {
        const name = appt?.patientName ?? "—";
        const newDateLabel = format(newDate, "dd 'de' MMMM", { locale: ptBR });
        const ok = await confirm({
          title: "Alterar consulta",
          description: (
            <>
              <div className="whitespace-nowrap">
                <span className="text-zinc-500">Paciente:</span>{" "}
                <span className="text-zinc-900">{name}</span>
              </div>
              <div className="whitespace-nowrap">
                <span className="text-zinc-500">Para:</span>{" "}
                <span className="font-semibold text-zinc-900">
                  {newDateLabel} · {newStart}
                </span>
              </div>
            </>
          ),
          confirmText: "Alterar",
          cancelText: "Cancelar",
        });
        if (!ok) {
          utils.appointment.invalidate();
          return;
        }
      }
      moveMutation.mutate(
        { id, date: newDate, startTime: newStart, endTime: newEnd, ...(trocouDeSala ? { salaId } : {}) },
        {
          onSuccess: () => {
            if (prevDate && prevStart && prevEnd) {
              const sameDay = prevDate.toDateString() === newDate.toDateString();
              const label =
                (sameDay ? `Movido pra ${newStart}` : `Movido pra ${format(newDate, "dd/MM", { locale: ptBR })} · ${newStart}`) +
                (nomeDaSala ? ` · ${nomeDaSala}` : "");
              toast.success(label, {
                action: {
                  label: "Desfazer",
                  onClick: () => {
                    moveMutation.mutate({
                      id,
                      date: prevDate,
                      startTime: prevStart,
                      endTime: prevEnd,
                      ...(trocouDeSala ? { salaId: prevSala } : {}),
                    });
                  },
                },
                duration: 5000,
              });
            }
          },
        },
      );
    },
    [settings?.confirmOnDrag, findAppointment, confirm, moveMutation, utils],
  );

  const handleStatusChange = useCallback(
    (id: string, status: AppointmentStatus) => {
      const data: Record<string, unknown> = { status };
      if (status === "CANCELLED") data.cancelledBy = "professional";
      updateMutation.mutate({ id, data: data as never });
      toast.success(`Status: ${APPOINTMENT_STATUS_LABELS[status]}`);
    },
    [updateMutation],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const appt = findAppointment(id);
      const name = appt?.patientName ?? "consulta";
      const ok = await confirm({
        title: `Excluir consulta de ${name}?`,
        description: "Essa ação é permanente e remove a consulta do histórico.",
        confirmText: "Excluir",
        cancelText: "Cancelar",
        destructive: true,
      });
      if (ok) removeMutation.mutate({ id });
    },
    [findAppointment, confirm, removeMutation],
  );

  const handleIniciarAtendimento = useCallback((id: string) => iniciarMutation.mutate({ id }), [iniciarMutation]);
  const handleEncerrarAtendimento = useCallback((id: string) => encerrarMutation.mutate({ id }), [encerrarMutation]);

  return {
    handleMove,
    handleStatusChange,
    handleDelete,
    handleIniciarAtendimento,
    handleEncerrarAtendimento,
    atendimentoPendente: iniciarMutation.isPending || encerrarMutation.isPending,
  };
}
